import {
  Component,
  OnInit,
  inject,
  signal,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DynamicDialogModule } from '@openng/optimus-ui/dynamicdialog';
import { SearchContainerComponent } from 'src/app/core/containers/search-container/search-container.component';
import { MonLog } from 'src/app/model/monlogs/mon-log';
import { forkJoin } from 'rxjs';
import { TabService } from 'src/app/core/services/tab.service';
import { PaginatorModule } from '@openng/optimus-ui/paginator';
import { AuthApiService } from 'src/app/services/api/auth-api.service';
import { MonlogsBuilderService } from 'src/app/services/builder/monlogs-builder.service';
import { MonlogsApiService } from 'src/app/services/api/monlogs-api.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MOBILE_MONLOG_DETAILS_CONTAINER } from 'src/app/constants/common.constants';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { BlockUIModule } from '@openng/optimus-ui/blockui';
import { PanelModule } from '@openng/optimus-ui/panel';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import {
  MonLogSearchResult,
  MonLogSortOrQuery,
} from 'src/app/model/monlogs/mon-log-search-result';

@Component({
  selector: 'app-mc-log-search',
  imports: [
    ProgressSpinnerComponent,
    TranslateModule,
    DynamicDialogModule,
    PaginatorModule,
    BlockUIModule,
    ReactiveFormsModule,
    PanelModule,
    ButtonModule,
    CardModule,
    DatePickerModule,
    InputTextModule,
  ],
  templateUrl: './mc-log-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './mc-log-search.component.scss',
})
export class McLogSearchComponent
  extends SearchContainerComponent<MonLogSearchResult>
  implements OnInit
{
  protected _translateService = inject(TranslateService);
  protected _authApi = inject(AuthApiService);
  protected _monLogBuilder = inject(MonlogsBuilderService);
  protected _monLogsApi = inject(MonlogsApiService);
  protected _logAndMessage = inject(LogAndMessageService);
  protected _breadCrumbService = inject(BreadCrumbBuilderService);
  protected _fb = inject(FormBuilder);
  protected _tabService = inject(TabService);

  pageLoading = signal(true);
  monLogs: WritableSignal<MonLogSearchResult[]> = signal([]);
  monLog: MonLog | null = null;
  filters = {};
  query = {};
  searchForm: FormGroup;
  pageSize: number = 15;
  currentPage: number = 0;

  constructor() {
    super();
    this.searchForm = this._fb.group({
      logId: [null],
      date: [null],
      facilityName: [null],
      facilityId: [null],
    });
  }

  ngOnInit(): void {
    const screenName$ = this._translateService.get('MONLOG.SCREEN.LOG_SEARCH');
    forkJoin([screenName$]).subscribe((label) => {
      this._tabService.updateActiveTabLabel(label[0]);
      this.screenName = label[0];
      this._breadCrumbService.resetAndAddBreadCrumb({
        label: this.screenName,
        routerLink: null,
      });
    });
    this.retrieveMonLogs();
  }

  retrieveMonLogs(): void {
    let selectedBusinessUnits = [];

    // Handle initial page load
    selectedBusinessUnits = this._authApi
      .getUserBusinessUnits()
      .map((bu) => bu.id);
    this.filters = this.searchForm.value;
    this.filters = {
      facilityName: this.searchForm.get('facilityName')?.value,
      facilityId: this.searchForm.get('facilityId')?.value,
      createdAt: this.searchForm.get('date')?.value,
      logId: this.searchForm.get('logId')?.value,
      businessUnits: selectedBusinessUnits,
    };

    this.query = this._monLogBuilder.buildMonLogSearchInput(this.filters);
    this.searchMonLogs(this.pageSize, this.currentPage, this.query);
  }

  searchMonLogs(
    pageSize: number,
    page: number,
    query: { [key: string]: any }
  ): void {
    page += 1;
    this.pageLoading.set(true);
    this._monLogsApi
      .queryForMonLogs(pageSize, page, query as MonLogSortOrQuery, {
        createdAt: 'DESC',
      })
      .subscribe({
        next: (connection) => {
          this.monLogs.set([...connection.items]);
          this.totalRecords = connection.totalRecords;
          this.elements = [...this.monLogs()];
          this.pageLoading.set(false);
        },
        error: (error) => {
          this.pageLoading.set(false);
          this._logAndMessage.errorLogOnly(error);
          this._logAndMessage.translateToErrorAlert({
            bodyKey: 'MONLOG.MESSAGES.ERROR.LOAD_LOGS',
            headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
          });
        },
      });
  }

  onPageChange($event: any) {
    this.currentPage = $event.page;
    this.pageSize = $event.rows;

    this.searchMonLogs(this.pageSize, this.currentPage, this.query);
  }

  clearFilters() {
    this.filters = {};
    this.searchForm.reset();
    this.currentPage = 0;
    this.retrieveMonLogs();
  }

  onMonLogSelected(monLog: MonLogSearchResult): void {
    if (monLog) {
      const newTab = this._tabService.buildNewTab(
        MOBILE_MONLOG_DETAILS_CONTAINER,
        true,
        undefined,
        monLog.id
      );
      this._tabService.setMobileTab(newTab);
    } else {
      this._logAndMessage.translateToErrorAlert({
        bodyKey: 'MONLOG.MESSAGES.ERROR.LOAD_MON_LOG',
        headerKey: 'COMMON.MESSAGES.HEADERS.ERROR',
      });
    }
  }
}
